import React, {ReactElement} from 'react'
import {useTranslation} from 'react-i18next'
import {ContactCenterCode} from '../../../app/types'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput from '../../../common/form/TextInput'
import FormRow from '../../../common/form/FormRow'
import FormFieldBox from '../../../common/form/FormFieldBox'
import SpinnableSubmitButton from '../../../common/form/SpinnableSubmitButton'
import {useFormSubmission} from '../../../common/servercall/useFormSubmission'
import ErrorAlert from '../../../common/components/ErrorAlert'
import {UseFormSetValue} from 'react-hook-form/dist/types/form'

const schema = yup
    .object()
    .shape({
        code: yup
            .string()
            .trim()
            .max(6, 'too-long')
            .required('required-m')
            .matches(/^[1-9]\d{1,6}$/, 'invalid-entry-number'),
        description: yup
            .string()
            .trim()
            .max(40, 'too-long')
            .required('required-m')
    })
    .required()

type Props = {
    entry: ContactCenterCode | null
    onSubmit: (formData: ContactCenterCode) => void
}

export type ReturnProps = {
    handleSubmit: () => void
    form: () => ReactElement
    submitButton: () => ReactElement
    reset: () => void
    setValue: UseFormSetValue<ContactCenterCode>
}

const useContactCenterCodeForm = ({ entry, onSubmit }: Props): ReturnProps => {
    const { t } = useTranslation()

    const { control, handleSubmit, setError, reset, setValue } = useForm<ContactCenterCode>({
        resolver: yupResolver(schema),
        defaultValues: entry
            ? {
                  ...entry,
              }
            : {},
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<ContactCenterCode>(onSubmit, setError)

    return {
        setValue: setValue,
        reset: reset,
        handleSubmit: handleSubmit(submit, onError),
        form: () =>
            entry ? (
                <>
                    <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
                    <FormRow>
                        <FormFieldBox fullWidth={true}>
                            <TextInput autofocus={true} label={t('contact-center-codes.code')} name={'code'} control={control} required={true} readonly={!!entry.uuid} />
                        </FormFieldBox>
                        <FormFieldBox fullWidth={true}>
                            <TextInput label={t('contact-center-codes.description')} name={'description'} control={control} required={true} />
                        </FormFieldBox>
                    </FormRow>
                </>
            ) : (
                <div />
            ),
        submitButton: () => (entry ? <SpinnableSubmitButton label={t(entry.uuid ? 'general.form-update' : 'general.form-create')} showSpinner={isSubmitting} /> : <></>),
    }
}

export default useContactCenterCodeForm
